import { useState, useRef, useEffect, useCallback } from "react";
import { Listing } from "src/types/listing";
import { useInkedEmbedUrl } from "./useInkedEmbedUrl";
import { useInkedBalance } from "./useInkedBalance";
import { useOptimisticBalanceUpdate } from "./useOptimisticBalanceUpdate";
import { useHubSpotTracking } from "src/hooks/useHubSpotTracking";

export const MAX_SEND_CARDS_LIMIT = 300;
export const DEFAULT_OWNER_NAME = "Current Owner";

interface InkedContact {
  first_name: string;
  last_name: string;
  id: string;
  address: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
  };
}

/** Check if an address has all required fields */
const isCompleteAddress = (address?: { streetAddress?: string; city?: string; state?: string; postalCode?: string }) => {
  return !!(address?.streetAddress && address?.city && address?.state && address?.postalCode);
};

/** Get missing fields from an address for logging purposes */
const getMissingAddressFields = (address?: { streetAddress?: string; city?: string; state?: string; postalCode?: string }): string[] => {
  if (!address) return ['entire address object'];
  const missing: string[] = [];
  if (!address.streetAddress) missing.push('streetAddress');
  if (!address.city) missing.push('city');
  if (!address.state) missing.push('state');
  if (!address.postalCode) missing.push('postalCode');
  return missing;
};

/** Get the best available address: owner address if complete, otherwise property address if complete */
const getBestAddress = (listing: Listing) => {
  const ownerAddress = listing.ownerContactInfo?.address;
  const propertyAddress = listing.address;
  const listingId = `${listing.productType}:${listing.id}`;

  // Prefer owner address if complete
  if (isCompleteAddress(ownerAddress)) {
    return ownerAddress;
  }

  // Log why owner address is invalid
  const ownerMissing = getMissingAddressFields(ownerAddress);
  console.log(`[SendCards] Listing ${listingId}: Owner address invalid, missing: ${ownerMissing.join(', ')}`);

  // Fall back to property address if complete
  if (isCompleteAddress(propertyAddress)) {
    console.log(`[SendCards] Listing ${listingId}: Falling back to property address`);
    return propertyAddress;
  }

  // Log why property address is also invalid
  const propertyMissing = getMissingAddressFields(propertyAddress);
  console.log(`[SendCards] Listing ${listingId}: Property address also invalid, missing: ${propertyMissing.join(', ')}`);
  console.log(`[SendCards] Listing ${listingId}: No valid address available - skipping`);

  return null;
};

const convertListingToInkedFormat = (listing: Listing): InkedContact => {
  const ownerInfo = listing.ownerContactInfo;
  const address = getBestAddress(listing);

  const hasFirstAndLast = ownerInfo?.firstName && ownerInfo?.lastName;

  // Use first_name/last_name if available, otherwise use fullName as first_name
  let firstName = ownerInfo?.firstName || "";
  let lastName = ownerInfo?.lastName || "";

  // Fall back to fullName if no first/last name available
  if (!hasFirstAndLast && ownerInfo?.fullName) {
    firstName = ownerInfo.fullName;
    lastName = "";
  }

  // Fall back to default owner name if no name is available at all
  if (!firstName) {
    firstName = DEFAULT_OWNER_NAME;
    lastName = "";
  }

  return {
    first_name: firstName,
    last_name: lastName,
    id: `${listing.productType}:${listing.id}`,
    address: {
      address1: address?.streetAddress || "",
      address2: "",
      city: address?.city || "",
      state: address?.state || "",
      country: "US",
      postcode: address?.postalCode || "",
    },
  };
};

/** A contact is valid for card sending if it has a complete address (owner or property) */
const isValidCardContact = (listing: Listing): boolean => {
  return getBestAddress(listing) !== null;
};

/** Generate a normalized key for deduplication based on name + address */
const getDedupeKey = (listing: Listing): string => {
  const address = getBestAddress(listing);
  const ownerInfo = listing.ownerContactInfo;

  // For default "Current Owner" name, use empty string so we dedupe by address only
  const hasRealName = ownerInfo?.firstName || ownerInfo?.fullName;
  const firstName = hasRealName
    ? (ownerInfo?.firstName || ownerInfo?.fullName || "").toLowerCase().trim()
    : "";
  const lastName = (ownerInfo?.lastName || "").toLowerCase().trim();

  const addr = (address?.streetAddress || "").toLowerCase().trim();
  const city = (address?.city || "").toLowerCase().trim();
  const state = (address?.state || "").toLowerCase().trim();
  const zip = (address?.postalCode || "").trim();

  return `${firstName}|${lastName}|${addr}|${city}|${state}|${zip}`;
};

/** Remove duplicate listings based on name + address, keeping first occurrence */
const deduplicateListings = (listings: Listing[]): { unique: Listing[]; duplicateCount: number } => {
  const seen = new Map<string, Listing>();

  for (const listing of listings) {
    const key = getDedupeKey(listing);
    if (!seen.has(key)) {
      seen.set(key, listing);
    }
  }

  return {
    unique: Array.from(seen.values()),
    duplicateCount: listings.length - seen.size,
  };
};

export const useSendCards = () => {
  const { trackEvent } = useHubSpotTracking();
  const { updateBalanceOptimistically } = useOptimisticBalanceUpdate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentListings, setCurrentListings] = useState<Listing[]>([]);
  const [templateCategory, setTemplateCategory] = useState<string>("");
  const [limitExceededInfo, setLimitExceededInfo] = useState<{
    cardCount: number;
    maxLimit: number;
  } | null>(null);
  const [validationInfo, setValidationInfo] = useState<{
    validCount: number;
    skippedCount: number;
    duplicateCount: number;
    totalCount: number;
  } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Tracks whether close event was already handled (cards sent or abandonment tracked)
  const closeEventHandledRef = useRef(false);

  // Only fetch embed URL when dialog is open AND no error/pending states
  const shouldFetchEmbedUrl = isOpen && !limitExceededInfo && !validationInfo;
  const { data: embedUrl, isLoading: isLoadingEmbed, error: embedError } = useInkedEmbedUrl(shouldFetchEmbedUrl);
  // Fetch balance to pass to Inked (Inked handles payment if insufficient)
  const { data: balanceData, refetch: refetchBalance, isLoading: isLoadingBalance, isFetching: isFetchingBalance } = useInkedBalance();

  // Open the composer dialog (called directly or after user confirms validation)
  const openComposer = useCallback(async (listings?: Listing[]) => {
    const listingsToUse = listings || currentListings;
    const product = listingsToUse[0]?.productType || "unknown";

    // Refetch balance to get the latest (for passing to Inked)
    await refetchBalance();

    // Track that we're opening the composer
    trackEvent("send_cards_inked_composer_opening", {
      cardCount: listingsToUse.length,
      product,
    });

    setIsOpen(true);
  }, [refetchBalance, currentListings, trackEvent]);

  const sendCards = useCallback(async (listings: Listing[], templateCategoryName = "", totalCount?: number) => {
    // Clear any previous errors
    setLimitExceededInfo(null);
    setValidationInfo(null);

    // Use totalCount if provided (avoids fetching all listings just to check count)
    const cardCount = totalCount ?? listings.length;
    const product = listings[0]?.productType || templateCategoryName || "unknown";

    // Track flow initiation
    trackEvent("send_cards_initiated", { cardCount, product });

    // Check if the number of cards exceeds the limit before fetching
    if (cardCount > MAX_SEND_CARDS_LIMIT) {
      trackEvent("send_cards_limit_exceeded_dialog_shown", { cardCount, maxLimit: MAX_SEND_CARDS_LIMIT });
      setLimitExceededInfo({
        cardCount,
        maxLimit: MAX_SEND_CARDS_LIMIT,
      });
      setIsOpen(true);
      return;
    }

    // Validate contacts - filter out those missing required name or address info
    const validListings = listings.filter(isValidCardContact);
    const skippedCount = listings.length - validListings.length;

    // Deduplicate valid contacts by name + address
    const { unique: uniqueListings, duplicateCount } = deduplicateListings(validListings);

    // Check deduplicated count against limit
    if (uniqueListings.length > MAX_SEND_CARDS_LIMIT) {
      trackEvent("send_cards_limit_exceeded", { cardCount: uniqueListings.length, maxLimit: MAX_SEND_CARDS_LIMIT });
      setLimitExceededInfo({
        cardCount: uniqueListings.length,
        maxLimit: MAX_SEND_CARDS_LIMIT,
      });
      setIsOpen(true);
      return;
    }

    // Store unique listings and the template category to preselect in Inked
    setCurrentListings(uniqueListings);
    setTemplateCategory(templateCategoryName);

    // If some contacts were skipped or duplicated, show confirmation dialog before proceeding
    if (skippedCount > 0 || duplicateCount > 0) {
      if (uniqueListings.length === 0) {
        trackEvent("send_cards_all_invalid_dialog_shown", { totalCount: listings.length });
      } else {
        trackEvent("send_cards_partial_send_dialog_shown", {
          validCount: uniqueListings.length,
          skippedCount,
          duplicateCount,
          totalCount: listings.length,
        });
      }
      setValidationInfo({
        validCount: uniqueListings.length,
        skippedCount,
        duplicateCount,
        totalCount: listings.length,
      });
      setIsOpen(true);
      return;
    }

    // No skipped or duplicate contacts - proceed directly to composer
    await openComposer(uniqueListings);
  }, [openComposer, trackEvent]);

  // Called when user confirms sending after seeing validation info
  const confirmSendCards = useCallback(async () => {
    trackEvent("send_cards_partial_send_dialog_confirmed", {
      cardCount: currentListings.length,
      product: currentListings[0]?.productType || "unknown",
    });
    setValidationInfo(null);
    await openComposer();
  }, [openComposer, trackEvent, currentListings]);

  const closeCardDialog = useCallback(() => {
    // Track dialog close with context about which step the user abandoned at
    if (validationInfo) {
      trackEvent("send_cards_abandoned", {
        step: "partial_send_dialog",
        cardCount: validationInfo.validCount,
        product: currentListings[0]?.productType || "unknown",
      });
    } else if (limitExceededInfo) {
      // Don't track as abandonment - user was blocked, not choosing to leave
    } else if (!closeEventHandledRef.current) {
      // User closed the composer dialog without sending cards (via Dialog X or click outside)
      trackEvent("send_cards_abandoned", {
        step: "inked_composer",
        cardCount: currentListings.length,
        product: currentListings[0]?.productType || "unknown",
      });
    }
    closeEventHandledRef.current = false;
    setIsOpen(false);
    setCurrentListings([]);
    setLimitExceededInfo(null);
    setValidationInfo(null);
  }, [trackEvent, validationInfo, limitExceededInfo, currentListings]);

  useEffect(() => {
    if (!isOpen) return;
    if (embedError) {
      console.error("Failed to get embed URL:", embedError);
      trackEvent("send_cards_embed_error", {
        cardCount: currentListings.length,
        product: currentListings[0]?.productType || "unknown",
      });
    }

    const sendListingsToCardApp = async () => {
      const hasData = currentListings.length > 0;
      const hasBalanceData = balanceData?.balance !== undefined && balanceData?.cardPrice !== undefined;

      if (!iframeRef.current || !hasData || !embedUrl || !hasBalanceData) {
        console.log(
          "Cannot send listings: missing iframe, data, embedUrl, or balanceData",
          {
            hasIframe: !!iframeRef.current,
            hasListings: currentListings.length > 0,
            hasEmbedUrl: !!embedUrl,
            hasBalanceData,
          }
        );
        return;
      }

      const contactsToSend: InkedContact[] = currentListings.map((listing) =>
        convertListingToInkedFormat(listing)
      );

      const targetWindow = iframeRef.current.contentWindow;
      if (!targetWindow) return;

      targetWindow.postMessage(
        {
          type: "COMPOSE_DATA",
          data: {
            contacts: contactsToSend,
            disclaimer_text: "Please be sure to follow all state and local regulations by identifying your brokerage and any other requirements for marketing materials",
            default_template_category_name: templateCategory,
            default_cover_category_name: "Real Estate",
            user_balance: balanceData.balance,
            card_price: balanceData.cardPrice,
          },
        },
        process.env.REACT_APP_INKED_APP_URL || ""
      );

    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== process.env.REACT_APP_INKED_APP_URL) return;

      const { type, data } = event.data;

      switch (type) {
        case "IFRAME_LOG":
          console.log("[INKED]:", event.data.message);
          break;

        case "CARD_APP_READY":
          console.log("Card app iframe is ready");
          trackEvent("send_cards_inked_composer_ready", {
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          if (iframeRef.current && embedUrl) {
            sendListingsToCardApp();
          }
          break;

        case "CARDS_SENT":
          console.log("Cards sent successfully:", data);
          closeEventHandledRef.current = true;
          trackEvent("send_cards_success", {
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          break;

        case "CANCEL_CLICK":
          console.log("Cancel clicked in card app");
          trackEvent("send_cards_abandoned", {
            step: "inked_composer",
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          // Mark as handled so closeCardDialog doesn't double-track
          closeEventHandledRef.current = true;
          closeCardDialog();
          break;

        case "CLOSE_CLICK":
          console.log("Close clicked in card app");
          // Only track as abandoned if cards weren't sent
          if (!closeEventHandledRef.current) {
            trackEvent("send_cards_abandoned", {
              step: "inked_composer",
              cardCount: currentListings.length,
              product: currentListings[0]?.productType || "unknown",
            });
          }
          // Mark as handled so closeCardDialog doesn't double-track
          closeEventHandledRef.current = true;
          closeCardDialog();
          break;

        case "CHECKOUT_SESSION_CREATED":
          console.log("Checkout session created in Inked:", data);
          trackEvent("send_cards_checkout_session_created", {
            amount: data?.amount,
            cardPrice: data?.cardPrice,
            userBalance: data?.userBalance,
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          break;

        case "CHECKOUT_SESSION_FAILED":
          console.log("Checkout session failed in Inked:", data);
          trackEvent("send_cards_checkout_session_failed", {
            error: data?.error,
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          break;

        case "PAYMENT_COMPLETED":
          console.log("Payment completed in Inked:", data);
          trackEvent("send_cards_payment_completed", {
            amount: data?.amount,
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          // Optimistically update balance cache and poll for confirmation
          if (data?.amount) {
            updateBalanceOptimistically(data.amount);
          }
          break;

        case "PAYMENT_CANCELLED":
          console.log("Payment cancelled in Inked:", data);
          trackEvent("send_cards_payment_cancelled", {
            amount: data?.amount,
            cardPrice: data?.cardPrice,
            userBalance: data?.userBalance,
            cardCount: currentListings.length,
            product: currentListings[0]?.productType || "unknown",
          });
          break;

        default:
          console.log("Unknown message from card app:", type);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isOpen, currentListings, embedUrl, closeCardDialog, embedError, templateCategory, trackEvent, updateBalanceOptimistically, balanceData?.balance, balanceData?.cardPrice]);

  // Get product type from current listings for tracking purposes
  const currentProduct = currentListings[0]?.productType || "unknown";

  return {
    sendCards,
    confirmSendCards,
    closeCardDialog,
    isOpen,
    currentListings,
    currentProduct,
    embedUrl: embedUrl || "",
    iframeRef,
    isLoadingEmbed,
    isLoadingBalance,
    isFetchingBalance,
    embedError,
    limitExceededInfo,
    validationInfo,
    balance: balanceData?.balance,
    cardPrice: balanceData?.cardPrice,
  };
};